import type { FieldType, Schema } from './schema-meta.ts';
import { DELIVERY_CODE_MAP } from './delivery-codes.ts';
import { CLEAR_CONDITION_CODE_MAP } from './clear-condition-codes.ts';
import { enumFromIndex, enumToIndex } from './enum-registry.ts';

// ── dict registry ─────────────────────────────────────────────────────
const DICTS: Record<string, { encode: Record<string, string>; decode: Record<string, string> }> =
  {};

function registerDict(name: string, encodeMap: Record<string, string>): void {
  const decode: Record<string, string> = {};
  for (const [k, v] of Object.entries(encodeMap)) decode[v] = k;
  DICTS[name] = { encode: encodeMap, decode };
}
registerDict('delivery', DELIVERY_CODE_MAP);
registerDict('clear-condition', CLEAR_CONDITION_CODE_MAP);
// 'asset' dict 는 phase 2 에서 user-state codec 도입할 때 등록.

// ── schema registry ──────────────────────────────────────────────────
const SCHEMAS: Record<string, Schema> = {};

export function registerSchema(schema: Schema): void {
  if (SCHEMAS[schema.name] && SCHEMAS[schema.name] !== schema) {
    throw new Error(`schema name collision: ${schema.name}`);
  }
  SCHEMAS[schema.name] = schema;
}

export function getSchema(name: string): Schema {
  const s = SCHEMAS[name];
  if (!s) throw new Error(`schema not registered: ${name}`);
  return s;
}

/** All registered schemas (master + nested sub-schemas). Used by the GS build
 *  step so Apps Script can resolve nested schema references. */
export function getAllSchemas(): Record<string, Schema> {
  return { ...SCHEMAS };
}

// ── per-field encode / decode ────────────────────────────────────────
function encodeField(type: FieldType, value: unknown): unknown {
  if (value === undefined || value === null) return null;
  if (type === 'string' || type === 'string?') return value;
  if (type === 'number' || type === 'number?') return value;
  if (type === 'boolean' || type === 'boolean?') return value;
  if (typeof type === 'object') {
    if (type.kind === 'enum') {
      return enumToIndex(type.members, value as string);
    }
    if (type.kind === 'dict') {
      const d = DICTS[type.dict];
      if (!d) throw new Error(`dict not registered: ${type.dict}`);
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[d.encode[k] ?? k] = v;
      }
      return out;
    }
    if (type.kind === 'array') {
      const inner = type.of;
      const arr = value as unknown[];
      if (typeof inner === 'string') {
        // 'number', 'string', 'boolean' (and optional variants) are primitives — use encodeField directly.
        if (
          inner === 'number' ||
          inner === 'number?' ||
          inner === 'string' ||
          inner === 'string?' ||
          inner === 'boolean' ||
          inner === 'boolean?'
        ) {
          return arr.map((v) => encodeField(inner, v));
        }
        const s = getSchema(inner);
        return arr.map((row) => encode(s, row as Record<string, unknown>));
      }
      return arr.map((v) => encodeField(inner, v));
    }
    if (type.kind === 'record') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = encodeField(type.value, v);
      }
      return out;
    }
    if (type.kind === 'json') return value;
  }
  return value;
}

function decodeField(type: FieldType, value: unknown): unknown {
  if (value === undefined || value === null) return value === null ? null : undefined;
  if (type === 'string' || type === 'string?') return value;
  if (type === 'number' || type === 'number?') return value;
  if (type === 'boolean' || type === 'boolean?') return value;
  if (typeof type === 'object') {
    if (type.kind === 'enum') {
      return enumFromIndex(type.members, value as number);
    }
    if (type.kind === 'dict') {
      const d = DICTS[type.dict];
      if (!d) throw new Error(`dict not registered: ${type.dict}`);
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[d.decode[k] ?? k] = v;
      }
      return out;
    }
    if (type.kind === 'array') {
      const inner = type.of;
      const arr = value as unknown[];
      if (typeof inner === 'string') {
        // 'number', 'string', 'boolean' (and optional variants) are primitives — use decodeField directly.
        if (
          inner === 'number' ||
          inner === 'number?' ||
          inner === 'string' ||
          inner === 'string?' ||
          inner === 'boolean' ||
          inner === 'boolean?'
        ) {
          return arr.map((v) => decodeField(inner, v));
        }
        const s = getSchema(inner);
        return arr.map((tup) => decode(s, tup as unknown[]));
      }
      return arr.map((v) => decodeField(inner, v));
    }
    if (type.kind === 'record') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = decodeField(type.value, v);
      }
      return out;
    }
    if (type.kind === 'json') return value;
  }
  return value;
}

// ── public API ───────────────────────────────────────────────────────
export function encode(schema: Schema, value: Record<string, unknown> | null): unknown[] | null {
  if (value === null) return null;
  const out: unknown[] = [];
  for (const [name, type] of schema.fields) {
    const v = value[name];
    out.push(v === undefined ? null : encodeField(type, v));
  }
  // 후행 null 제거 — 짧은 tuple 로 바이트 절약.
  while (out.length > 0 && (out[out.length - 1] === null || out[out.length - 1] === undefined)) {
    out.pop();
  }
  return out;
}

export function decode<T>(schema: Schema, tuple: unknown[] | null): T | null {
  if (tuple === null) return null;
  const out: Record<string, unknown> = {};
  for (let i = 0; i < schema.fields.length; i++) {
    const [name, type] = schema.fields[i];
    const slot = i < tuple.length ? tuple[i] : undefined;
    out[name] = slot === undefined ? undefined : decodeField(type, slot);
  }
  return out as T;
}

export function roundTrip<T>(schema: Schema, value: T): T {
  return decode(schema, encode(schema, value as never)) as T;
}
